import React, { useCallback, useEffect, useRef } from "react";
import { MapStyleId } from "../../types";
import { getMapStyleInfo } from "../../config/mapStyles";

export interface MapStyleOption {
  id: MapStyleId;
  name: string;
  icon: string;
  available: boolean;
}

interface MapStyleButtonProps {
  // Primary data - can provide either styleId OR style object
  styleId?: MapStyleId;
  style?: MapStyleOption;

  // State
  isSelected: boolean;
  isStarred: boolean;
  isUnavailable?: boolean; // For styleId mode

  // Callbacks
  onSelect: ((id: MapStyleId) => void) | (() => void);
  onToggleStar: ((id: MapStyleId) => void) | (() => void);

  // Interaction mode
  enableLongPress?: boolean; // If true, long press to star; if false, click right side to star

  // Styling mode
  variant?: "detailed" | "compact"; // detailed = SettingsModal style, compact = QuickIsland style
}

export const MapStyleButton: React.FC<MapStyleButtonProps> = ({
  styleId: propStyleId,
  style: propStyle,
  isSelected,
  isStarred,
  isUnavailable,
  onSelect,
  onToggleStar,
  enableLongPress = false,
  variant = "detailed",
}) => {
  // Derive style info
  const styleId = propStyleId || propStyle?.id;
  const styleInfo = styleId ? getMapStyleInfo(styleId) : null;
  const styleName = propStyle?.name || styleInfo?.name || "";
  const styleIcon = propStyle?.icon || styleInfo?.icon || "🗺️";
  const styleDesc =
    styleInfo?.desc || styleId?.split(":")[0].toUpperCase() || "";
  const available =
    propStyle?.available !== undefined ? propStyle.available : !isUnavailable;

  // Long press handling (for compact/QuickIsland mode)
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const isLongPress = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });

  const handleLongPressStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!enableLongPress) return;

      isLongPress.current = false;

      if ("touches" in e) {
        startPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else {
        startPos.current = { x: e.clientX, y: e.clientY };
      }

      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }

      longPressTimer.current = setTimeout(() => {
        isLongPress.current = true;
        if (styleId) {
          (onToggleStar as (id: MapStyleId) => void)(styleId);
        } else {
          (onToggleStar as () => void)();
        }
        longPressTimer.current = null;
      }, 500);
    },
    [enableLongPress, styleId, onToggleStar],
  );

  const handleMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!enableLongPress || !longPressTimer.current) return;

      let currentX = 0;
      let currentY = 0;
      if ("touches" in e) {
        currentX = e.touches[0].clientX;
        currentY = e.touches[0].clientY;
      } else {
        currentX = e.clientX;
        currentY = e.clientY;
      }

      if (
        Math.abs(currentX - startPos.current.x) > 10 ||
        Math.abs(currentY - startPos.current.y) > 10
      ) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    },
    [enableLongPress],
  );

  const handleLongPressEnd = useCallback(() => {
    if (!enableLongPress) return;

    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, [enableLongPress]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!available) return;

      e.preventDefault();
      e.stopPropagation();

      if (enableLongPress) {
        // Long press mode: click selects (unless it was a long press)
        if (!isLongPress.current) {
          if (styleId) {
            (onSelect as (id: MapStyleId) => void)(styleId);
          } else {
            (onSelect as () => void)();
          }
        }
        isLongPress.current = false;
      } else {
        // Click area mode: detect if click is on star area (right 30px)
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;

        if (clickX > rect.width - 30) {
          if (styleId) {
            (onToggleStar as (id: MapStyleId) => void)(styleId);
          } else {
            (onToggleStar as () => void)();
          }
        } else {
          if (styleId) {
            (onSelect as (id: MapStyleId) => void)(styleId);
          } else {
            (onSelect as () => void)();
          }
        }
      }
    },
    [available, enableLongPress, styleId, onSelect, onToggleStar],
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  // Helper for style name formatting
  const getStyleName = () => {
    if (styleName) return styleName;
    if (!styleId) return "";
    const [, name] = styleId.split(":");
    return name
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  if (variant === "compact") {
    // Compact mode (QuickIsland style) - inline styles
    return (
      <button
        onClick={handleClick}
        onMouseDown={handleLongPressStart}
        onMouseUp={handleLongPressEnd}
        onMouseMove={handleMove}
        onMouseLeave={handleLongPressEnd}
        onTouchStart={handleLongPressStart}
        onTouchEnd={handleLongPressEnd}
        onTouchMove={handleMove}
        onTouchCancel={handleLongPressEnd}
        disabled={!available}
        title={`${getStyleName()} - ${available ? "Tap to select, hold to star" : "Locked"}`}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "6px 2px",
          width: "100%",
          background: isSelected
            ? "rgba(62, 106, 225, 0.3)"
            : "rgba(255,255,255,0.05)",
          border: isSelected
            ? "1px solid var(--accent)"
            : "1px solid rgba(255,255,255,0.1)",
          borderRadius: "8px",
          cursor: available ? "pointer" : "not-allowed",
          opacity: available ? 1 : 0.4,
          transition: "all 0.2s",
          position: "relative",
        }}
      >
        <div style={{ fontSize: "18px", lineHeight: 1 }}>{styleIcon}</div>
        <div
          style={{
            fontSize: "9px",
            marginTop: "2px",
            color: isSelected ? "var(--text-primary)" : "var(--text-secondary)",
            fontWeight: isSelected ? 600 : 400,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
            textAlign: "center",
          }}
        >
          {getStyleName().split(" ")[0]}
        </div>
        {isStarred && (
          <div
            style={{
              position: "absolute",
              top: "-2px",
              right: "-2px",
              fontSize: "8px",
              background: "var(--bg-primary)",
              borderRadius: "50%",
              padding: "1px",
            }}
          >
            ⭐
          </div>
        )}
        {!available && (
          <div
            style={{
              position: "absolute",
              top: "2px",
              left: "2px",
              fontSize: "8px",
            }}
          >
            🔒
          </div>
        )}
      </button>
    );
  }

  // Detailed mode (SettingsModal style) - CSS classes
  return (
    <button
      className={`map-style-button ${isSelected ? "selected" : ""} ${!available ? "unavailable" : ""}`}
      onClick={handleClick}
      onMouseDown={enableLongPress ? handleLongPressStart : undefined}
      onMouseUp={enableLongPress ? handleLongPressEnd : undefined}
      onMouseMove={enableLongPress ? handleMove : undefined}
      onMouseLeave={enableLongPress ? handleLongPressEnd : undefined}
      onTouchStart={enableLongPress ? handleLongPressStart : undefined}
      onTouchEnd={enableLongPress ? handleLongPressEnd : undefined}
      onTouchMove={enableLongPress ? handleMove : undefined}
      onTouchCancel={enableLongPress ? handleLongPressEnd : undefined}
      type="button"
    >
      <div className="map-style-info">
        <div className="map-style-name">
          <span className="map-style-icon">{styleIcon}</span>
          {getStyleName()}
          {!available && <span className="lock-icon">🔒</span>}
        </div>
        <div className="map-style-desc">{styleDesc}</div>
      </div>
      <div className={`star-icon ${isStarred ? "starred" : ""}`}>⭐</div>
    </button>
  );
};
